import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from 'lucide-react';
import { useUIColors } from '../../hooks/useTheme';
import { parseDateSafe } from './periodSummaryUtils';
import {
  getSalePurchaseOrderFilterState,
  getSalePurchaseOrderLabel,
  isSaleWithoutPurchaseOrder,
} from './salePurchaseOrderUtils';
import {
  formatSaleBillingCell,
  formatSaleDispatchCell,
  getSaleCreditDebitFolios,
  getSaleDispatchGuides,
  getSaleInvoiceFolios,
} from './saleTableDisplayUtils';

const SALE_STATUS_STYLES = {
  'En Preparación': { bg: '#facc15', text: '#422006', border: '#a16207' },
  'En Ruta': { bg: '#ea580c', text: '#fff7ed', border: '#9a3412' },
  Recepcionado: { bg: '#0f766e', text: '#f0fdfa', border: '#115e59' },
  'Facturado Parcial': { bg: '#f59e0b', text: '#fffbeb', border: '#b45309' },
  Facturado: { bg: '#1d4ed8', text: '#eff6ff', border: '#1e3a8a' },
  Pagado: { bg: '#15803d', text: '#f0fdf4', border: '#14532d' },
  default: { bg: '#52525b', text: '#fafafa', border: '#27272a' },
};

const SALE_STATUS_ORDER = [
  'En Preparación',
  'En Ruta',
  'Recepcionado',
  'Facturado Parcial',
  'Facturado',
  'Pagado',
];

const formatDateEs = (iso) => {
  const d = parseDateSafe(iso);
  if (!d) return '—';
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatMoney = (value, currency = 'CLP') => {
  const n = Number(value || 0);
  const sym = currency === 'USD' ? 'US$' : currency === 'EUR' ? '€' : '$';
  return `${sym} ${n.toLocaleString('es-CL', { maximumFractionDigits: 0 })}`;
};

/** Total neto → total con IVA (19%). */
const grossAmountConIva = (netTotal) => {
  const t = Number(netTotal || 0);
  if (!Number.isFinite(t) || t === 0) return 0;
  return Math.round(t * 1.19 * 100) / 100;
};

const normalizeSaleStatus = (s = '') => {
  const raw = String(s || '').trim();
  if (SALE_STATUS_ORDER.includes(raw)) return raw;
  return 'En Preparación';
};

const PO_FILTER_OPTIONS = [
  { value: '', label: 'Todas' },
  { value: 'with_po', label: 'Con OC' },
  { value: 'without_po', label: 'Sin OC' },
  { value: 'pending', label: 'OC pendiente' },
];

const resolveInvoiceFolio = (row) => {
  const folios = getSaleInvoiceFolios(row);
  return folios[0] || '';
};

const resolveInvoiceDate = (row) =>
  row?.invoice_date
  || (Array.isArray(row?.linked_document_dates) && row.linked_document_dates[0])
  || '';

const TABLE_COLUMNS = [
  { key: 'Venta', align: 'left', sortKey: 'sale_code' },
  { key: 'Cliente', align: 'left', sortKey: 'organization_name' },
  { key: 'Cierre', align: 'left', sortKey: 'deal_close_date' },
  { key: 'Montos', align: 'right', sortKey: 'total_amount' },
  { key: 'Gal. T-COAT', align: 'right', sortKey: 'tcoat_gallons' },
  { key: 'Orden de compra', align: 'left', sortKey: 'purchase_order_code' },
  { key: 'Despachos', align: 'left', sortKey: 'legs_count' },
  { key: 'Facturación', align: 'left', sortKey: 'invoice_number' },
  { key: 'Estado', align: 'center', sortKey: 'sale_status' },
];

const INVOICE_FILTER_OPTIONS = [
  { value: '', label: 'Todas' },
  { value: 'pending', label: 'Sin factura' },
  { value: 'invoiced', label: 'Con factura' },
];

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'Todos' },
  ...SALE_STATUS_ORDER.map((value) => ({ value, label: value })),
];

const getSortValue = (row, sortKey) => {
  switch (sortKey) {
    case 'sale_code':
      return String(row.sale_code || row.quotation_code || '').toLowerCase();
    case 'organization_name':
      return String(row.organization_name || '').toLowerCase();
    case 'deal_close_date':
      return String(row.deal_close_date || '');
    case 'total_amount':
      return Number(row.total_amount) || 0;
    case 'tcoat_gallons':
      return Number(row.tcoat_gallons) || 0;
    case 'purchase_order_code':
      return getSalePurchaseOrderLabel(row).toLowerCase();
    case 'legs_count':
      return Number(row.legs_count) || 0;
    case 'invoice_number':
      return getSaleInvoiceFolios(row).join(' ').toLowerCase();
    case 'sale_status': {
      const st = normalizeSaleStatus(row.sale_status);
      const idx = SALE_STATUS_ORDER.indexOf(st);
      return idx >= 0 ? idx : 0;
    }
    default:
      return '';
  }
};

const compareRows = (a, b, sortKey, sortDir) => {
  const mult = sortDir === 'asc' ? 1 : -1;
  const va = getSortValue(a, sortKey);
  const vb = getSortValue(b, sortKey);
  if (typeof va === 'number' && typeof vb === 'number') {
    return (va - vb) * mult;
  }
  return String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' }) * mult;
};

/**
 * Tabla de ventas (A008_sales): mismo look que Table3 / QuotationsTable3.
 */
const formatGallons = (value) => {
  const n = Number(value || 0);
  if (!(n > 0)) return '—';
  return n.toLocaleString('es-CL', { maximumFractionDigits: 1 });
};

const SalesTable3 = ({
  rows = [],
  loading = false,
  error = '',
  isDarkMode = false,
  hideChrome = false,
  onOpenSale,
  gallonsByQuotationId = null,
}) => {
  const ui = useUIColors();
  void isDarkMode;
  const bg = ui.page;
  const headerBg = ui.header;
  const border = ui.border;
  const text = ui.text;
  const muted = ui.muted;
  const rowHover = ui.rowHover;
  const padV = 9;
  const padH = 10;
  const thPadV = 8;

  const inputBg = ui.inputBg;
  const inputBorder = ui.inputBorder;

  const [searchQuery, setSearchQuery] = useState('');
  const [filterInvoice, setFilterInvoice] = useState('');
  const [filterPurchaseOrder, setFilterPurchaseOrder] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortColumn, setSortColumn] = useState('sale_code');
  const [sortDir, setSortDir] = useState('desc');

  const selectStyle = {
    height: '36px',
    padding: '0 10px',
    borderRadius: '8px',
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: text,
    fontSize: '13px',
    minWidth: '160px',
    outline: 'none',
  };

  const rowsWithGallons = useMemo(() => {
    if (!gallonsByQuotationId) return rows;
    return (rows || []).map((row) => ({
      ...row,
      tcoat_gallons:
        row.tcoat_gallons != null
          ? Number(row.tcoat_gallons) || 0
          : gallonsByQuotationId.get(Number(row.quotation_id)) || 0,
    }));
  }, [rows, gallonsByQuotationId]);

  const filteredRows = useMemo(() => {
    let list = rowsWithGallons;

    const q = searchQuery.trim().toLowerCase();
    const tokens = q.split(/\s+/).filter(Boolean);
    if (tokens.length) {
      list = list.filter((row) => {
        const hay = [
          row.sale_code,
          row.quotation_code,
          row.quotation_date,
          row.deal_close_date,
          row.organization_name,
          row.contact_name,
          row.user_name,
          row.total_amount,
          row.currency,
          getSalePurchaseOrderLabel(row),
          isSaleWithoutPurchaseOrder(row) ? 'sin oc sin orden de compra' : '',
          resolveInvoiceFolio(row),
          getSaleInvoiceFolios(row).join(' '),
          getSaleCreditDebitFolios(row).join(' '),
          getSaleDispatchGuides(row).join(' '),
          ...(Array.isArray(row.linked_document_numbers) ? row.linked_document_numbers : []),
          ...(Array.isArray(row.linked_invoice_numbers) ? row.linked_invoice_numbers : []),
          ...(Array.isArray(row.linked_credit_debit_numbers) ? row.linked_credit_debit_numbers : []),
          ...(Array.isArray(row.delivery_dispatch_guides) ? row.delivery_dispatch_guides : []),
          row.legs_count,
          row.deliveries_count,
          row.dispatch_cost_total,
          resolveInvoiceDate(row),
          ...(Array.isArray(row.linked_document_dates) ? row.linked_document_dates : []),
          row.invoice_date,
          row.dispatch_guide,
          row.sale_status,
          row.quotation_status,
          row.estimated_cost,
        ]
          .filter((x) => x !== undefined && x !== null)
          .map((x) => String(x))
          .join(' ')
          .toLowerCase();
        return tokens.every((t) => hay.includes(t));
      });
    }

    if (filterInvoice === 'pending') {
      list = list.filter((row) => !String(row.invoice_number || '').trim() && Number(row.linked_document_count || 0) === 0);
    } else if (filterInvoice === 'invoiced') {
      list = list.filter((row) => String(row.invoice_number || '').trim() || Number(row.linked_document_count || 0) > 0);
    }

    if (filterPurchaseOrder === 'without_po') {
      list = list.filter((row) => getSalePurchaseOrderFilterState(row) === 'without_po');
    } else if (filterPurchaseOrder === 'with_po') {
      list = list.filter((row) => getSalePurchaseOrderFilterState(row) === 'with_po');
    } else if (filterPurchaseOrder === 'pending') {
      list = list.filter((row) => getSalePurchaseOrderFilterState(row) === 'pending');
    }

    if (filterStatus) {
      list = list.filter((row) => normalizeSaleStatus(row.sale_status) === filterStatus);
    }

    return list;
  }, [rowsWithGallons, searchQuery, filterInvoice, filterPurchaseOrder, filterStatus]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a, b) => compareRows(a, b, sortColumn, sortDir));
    return arr;
  }, [filteredRows, sortColumn, sortDir]);

  const handleSortClick = (key) => {
    if (sortColumn === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(key);
      setSortDir('asc');
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterInvoice('');
    setFilterPurchaseOrder('');
    setFilterStatus('');
  };

  const hasActiveFilters = Boolean(searchQuery.trim())
    || Boolean(filterInvoice)
    || Boolean(filterPurchaseOrder)
    || Boolean(filterStatus);
  const subtitle = loading
    ? 'Cargando…'
    : error ||
      (hasActiveFilters
        ? `Mostrando ${sortedRows.length} de ${rows.length} ventas · A008_sales`
        : `${rows.length} ventas · cotizaciones adjudicadas · A008_sales`);

  const colCount = TABLE_COLUMNS.length;

  return (
    <div
      style={
        hideChrome
          ? { background: bg }
          : {
              border: `1px solid ${border}`,
              borderRadius: '12px',
              overflow: 'hidden',
              background: bg,
            }
      }
    >
      {hideChrome ? null : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: `1px solid ${border}`,
            background: headerBg,
            flexWrap: 'wrap',
            gap: '10px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: text }}>Ventas</h3>
            <p style={{ margin: 0, fontSize: '12px', color: muted }}>{subtitle}</p>
          </div>
        </div>
      )}

      <div
        style={{
          padding: '10px 16px',
          borderBottom: `1px solid ${border}`,
          background: bg,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'flex-end',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 220px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Buscar
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Search size={16} color={muted} aria-hidden />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Código venta, cotización, cliente, factura…"
              style={{ ...selectStyle, flex: 1, minWidth: 0 }}
            />
          </span>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '160px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Factura
          </span>
          <select
            value={filterInvoice}
            onChange={(e) => setFilterInvoice(e.target.value)}
            style={selectStyle}
          >
            {INVOICE_FILTER_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '160px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Orden de compra
          </span>
          <select
            value={filterPurchaseOrder}
            onChange={(e) => setFilterPurchaseOrder(e.target.value)}
            style={selectStyle}
          >
            {PO_FILTER_OPTIONS.map((o) => (
              <option key={o.value || 'all-po'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '170px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Estado
          </span>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={selectStyle}
          >
            {STATUS_FILTER_OPTIONS.map((o) => (
              <option key={o.value || 'all-status'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            style={{
              height: '36px',
              padding: '0 12px',
              borderRadius: '8px',
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: muted,
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              marginBottom: '2px',
            }}
          >
            Limpiar filtros
          </button>
        ) : null}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1380px' }}>
          <thead>
            <tr style={{ background: headerBg }}>
              {TABLE_COLUMNS.map(({ key: h, align, sortKey: sk }) => (
                <th
                  key={h}
                  scope="col"
                  onClick={() => sk && handleSortClick(sk)}
                  onKeyDown={(e) => {
                    if (!sk) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSortClick(sk);
                    }
                  }}
                  tabIndex={sk ? 0 : undefined}
                  style={{
                    textAlign: align,
                    padding: `${thPadV}px ${padH + 4}px`,
                    fontSize: '11px',
                    fontWeight: 700,
                    color: muted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    borderBottom: `1px solid ${border}`,
                    cursor: sk ? 'pointer' : 'default',
                    userSelect: 'none',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
                      gap: '6px',
                      width: align === 'center' || align === 'right' ? '100%' : 'auto',
                    }}
                  >
                    {h}
                    {sk ? (
                      sortColumn === sk ? (
                        sortDir === 'asc' ? (
                          <ArrowUp size={13} strokeWidth={2.5} aria-hidden />
                        ) : (
                          <ArrowDown size={13} strokeWidth={2.5} aria-hidden />
                        )
                      ) : (
                        <ArrowUpDown size={13} strokeWidth={2} style={{ opacity: 0.4 }} aria-hidden />
                      )
                    ) : null}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount} style={{ padding: '28px', textAlign: 'center', color: muted }}>
                  Cargando ventas…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={colCount} style={{ padding: '28px', textAlign: 'center', color: muted }}>
                  {error || 'No hay ventas. Las cotizaciones en estado Adjudicada aparecen aquí al sincronizar.'}
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={colCount} style={{ padding: '28px', textAlign: 'center', color: muted }}>
                  Ningún resultado con los filtros actuales.
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => {
                const st = normalizeSaleStatus(row.sale_status);
                const palette = SALE_STATUS_STYLES[st] || SALE_STATUS_STYLES.default;
                const currency = row.currency || 'CLP';
                const total = Number(row.total_amount || 0);
                const gross = grossAmountConIva(total);
                const cost = Number(row.estimated_cost || 0);
                const statusLabel = String(row.quotation_status || '').trim();
                const isAdjudicada = statusLabel === 'Adjudicada';
                const saleCodeLabel = String(row.sale_code || '').trim()
                  ? row.sale_code
                  : isAdjudicada
                    ? 'Sin código'
                    : '—';
                const initial = String(row.sale_code || row.quotation_code || '?').trim().charAt(0).toUpperCase();

                const dispatchCell = formatSaleDispatchCell(row);
                const billingCell = formatSaleBillingCell(row);

                return (
                  <tr
                    key={row.id}
                    style={{
                      borderBottom: `1px solid ${border}`,
                      transition: 'background 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = rowHover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <td style={{ padding: `${padV}px 0`, verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', alignItems: 'stretch', minHeight: '36px' }}>
                        <div
                          style={{
                            width: '3px',
                            flexShrink: 0,
                            background: palette.border,
                            borderRadius: '0 2px 2px 0',
                          }}
                        />
                        <div style={{ display: 'flex', gap: '6px', padding: `2px ${padH + 2}px`, alignItems: 'center' }}>
                          <div
                            style={{
                              width: '26px',
                              height: '26px',
                              borderRadius: '7px',
                              background: isDarkMode ? '#27272a' : '#f0ebe3',
                              display: 'grid',
                              placeItems: 'center',
                              fontWeight: 800,
                              fontSize: '12px',
                              color: text,
                              flexShrink: 0,
                            }}
                          >
                            {initial}
                          </div>
                          <div>
                            <button
                              type="button"
                              onClick={() => onOpenSale?.(row)}
                              style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                margin: 0,
                                cursor: 'pointer',
                                fontWeight: 700,
                                fontSize: '12px',
                                color: '#2563eb',
                                lineHeight: 1.2,
                                textAlign: 'left',
                              }}
                            >
                              {saleCodeLabel}
                            </button>
                            <div style={{ fontSize: '11px', color: '#2563eb', fontWeight: 600, marginTop: '2px' }}>
                              {row.quotation_code || '—'}
                              {isAdjudicada ? (
                                <span style={{ color: muted, fontWeight: 500 }}> · Adjudicada</span>
                              ) : null}
                            </div>
                            <div style={{ fontSize: '11px', color: muted, marginTop: '1px' }}>
                              Cot.: {formatDateEs(row.quotation_date)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', maxWidth: '220px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: text, lineHeight: 1.2 }}>
                        {row.organization_name || '—'}
                      </div>
                      <div style={{ fontSize: '11px', color: muted, marginTop: '2px' }}>{row.contact_name || '—'}</div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', maxWidth: '140px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: text }}>{formatDateEs(row.deal_close_date)}</div>
                      <div style={{ fontSize: '11px', color: muted, marginTop: '2px' }}>Cierre trato</div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 700, fontSize: '13px', color: '#ea580c', lineHeight: 1.2 }}>
                        {formatMoney(gross, currency)}
                      </div>
                      <div style={{ fontSize: '11px', fontWeight: 500, color: muted, marginTop: '2px', lineHeight: 1.2 }}>
                        Sin IVA: {formatMoney(total, currency)}
                      </div>
                      <div style={{ fontSize: '11px', color: muted, marginTop: '2px' }}>
                        Costo est.: {formatMoney(cost, currency)}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        verticalAlign: 'middle',
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: '13px', color: text, lineHeight: 1.2 }}>
                        {formatGallons(row.tcoat_gallons)}
                      </div>
                      <div style={{ fontSize: '11px', color: muted, marginTop: '2px' }}>gal T-COAT</div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', maxWidth: '200px' }}>
                      {isSaleWithoutPurchaseOrder(row) ? (
                        <div style={{ fontSize: '12px', fontWeight: 700, color: text }}>
                          Sin Orden de Compra
                        </div>
                      ) : (
                        <>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: text }}>
                            {getSalePurchaseOrderLabel(row) || '—'}
                          </div>
                          <div style={{ fontSize: '11px', color: muted, marginTop: '2px', lineHeight: 1.25 }}>
                            Rec.: {formatDateEs(row.purchase_order_received_date)}
                          </div>
                          <div style={{ fontSize: '11px', color: muted, lineHeight: 1.25 }}>
                            Acept.: {formatDateEs(row.purchase_order_accepted_date)}
                          </div>
                        </>
                      )}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', maxWidth: '180px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: text, lineHeight: 1.25 }}>
                        {dispatchCell.legsLine}
                      </div>
                      <div style={{ fontSize: '11px', color: muted, marginTop: '2px', lineHeight: 1.25 }}>
                        {dispatchCell.deliveriesLine}
                      </div>
                      <div style={{ fontSize: '11px', color: muted, marginTop: '2px', lineHeight: 1.25 }}>
                        Costo: {formatMoney(dispatchCell.costLine, currency)}
                      </div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', maxWidth: '240px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: text, lineHeight: 1.25 }}>
                        {billingCell.invoicesLine}
                      </div>
                      <div style={{ fontSize: '11px', color: muted, marginTop: '2px', lineHeight: 1.25 }}>
                        {billingCell.guidesLine}
                      </div>
                      <div style={{ fontSize: '11px', color: muted, marginTop: '2px', lineHeight: 1.25 }}>
                        {billingCell.creditDebitLine}
                      </div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', textAlign: 'center', maxWidth: '180px' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '4px 10px',
                          borderRadius: '999px',
                          fontSize: '11px',
                          fontWeight: 700,
                          background: palette.bg,
                          color: palette.text,
                          border: `1px solid ${palette.border}`,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {st}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SalesTable3;
